import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, day } from "../../api.js";
import { useResource } from "../useResource.js";
import { shortName } from "../common.jsx";
import { SuggestInput } from "./Suggest.jsx";
import PnRows from "./PnRows.jsx";
import PnFromPrimary from "./PnFromPrimary.jsx";
import PnForm from "./PnForm.jsx";
import { KINDS, NONPAYER, registrationDeadline } from "../../../../lib/pn/model.js";
import { validatePn } from "../../../../lib/pn/validate.js";
import { totals, kopUi, RATES } from "../../../../lib/pn/calc.js";
import "./pn.css";
import Money from "../../components/Money.jsx";

// Майстер податкової накладної: ① операція й покупець → ② товари → ③ перевірка → ④ податкова накладна
// на бланку затвердженої форми (обов'язково перед підписом). Чернетка автоматично зберігається лише в
// eCabinet; у ДПС нічого не створюється й не надсилається.
const STEPS = ["Операція й покупець", "Товари та послуги", "Перевірка", "Податкова накладна"];
const stepOf = (path) => (path.startsWith("rows") ? 2 : 1);
const fid = (path) => `pn-${path.replaceAll(".", "-")}`;
const ua = (iso) => (iso ? iso.split("-").reverse().join(".") : "");
const todayKyiv = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Kyiv" });

export default function PnWizard({ companyId, company, draftId, onRelogin }) {
  const canEdit = ["preparer", "submitter", "owner"].includes(company.role);
  const [doc, setDoc] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [step, setStep] = useState(1);
  const [shown, setShown] = useState({}); // з якого кроку вже показуємо помилки полів
  const [save, setSave] = useState({ state: "saved" });
  const [docKey, setDocKey] = useState(0);
  const [savedVer, setSavedVer] = useState(0); // версія збереженої чернетки — «Оракул» на кроці 2 перераховує після кожного збереження
  const [buyerCheck, setBuyerCheck] = useState(null);
  const version = useRef(0), pending = useRef(null), saving = useRef(false), timer = useRef(null);
  const pre = useResource(`/api/companies/${companyId}/pn/prefill`);

  useEffect(() => {
    api(`/api/companies/${companyId}/pn/drafts/${draftId}`).then(({ draft }) => {
      version.current = draft.version; setSavedVer(draft.version); setDoc(draft.doc);
      if (draft.reviewed && draft.reviewed.version === draft.version) setStep(4);
    }, setLoadError);
  }, [companyId, draftId]);

  // Автозбереження: послідовно, з поточною версією; конфлікт (інша вкладка) — повідомляємо, не перезаписуємо
  const flush = useCallback(async () => {
    if (saving.current || !pending.current) return;
    saving.current = true;
    const d = pending.current; pending.current = null;
    setSave({ state: "saving" });
    try {
      const r = await api(`/api/companies/${companyId}/pn/drafts/${draftId}`, { method: "PUT", body: { doc: d, version: version.current } });
      version.current = r.draft.version;
      if (!pending.current) setSavedVer(r.draft.version);
      setSave({ state: "saved", at: new Date() });
    } catch (e) {
      setSave({ state: "error", error: e, conflict: e instanceof ApiError && e.status === 409 });
    } finally {
      saving.current = false;
      if (pending.current) flush();
    }
  }, [companyId, draftId]);
  const update = useCallback((fn) => {
    setDoc((d) => {
      const n = typeof fn === "function" ? fn(d) : fn;
      pending.current = n;
      clearTimeout(timer.current); timer.current = setTimeout(flush, 600);
      setSave((s) => (s.state === "error" && s.conflict ? s : { state: "dirty" }));
      return n;
    });
  }, [flush]);
  const flushNow = async () => {
    clearTimeout(timer.current);
    await flush();
    while (saving.current || pending.current) { await new Promise((r) => setTimeout(r, 60)); if (!saving.current) await flush(); }
  };

  const [stuck, setStuck] = useState([]);
  // Чернетка запам'ятовує продавця при створенні: порожні реквізити (РНОКПП керівника, ІПН, ДПС) дописуємо з даних
  // компанії, щойно вони з'явились (напр. РНОКПП внесли в «Дані компанії»)
  useEffect(() => {
    const ps = pre.data?.seller;
    if (!doc || !ps || !canEdit) return;
    const s = doc.seller || {}, d = s.director || {};
    const fill = { ...s, ...Object.fromEntries(["name", "ipn", "tin", "ks", "reg", "raj", "sti"].filter((k) => !s[k] && ps[k]).map((k) => [k, ps[k]])),
      director: { name: d.name || ps.director?.name || "", rnokpp: /^\d{10}$/.test(String(d.rnokpp || "")) ? d.rnokpp : ps.director?.rnokpp || d.rnokpp || "" } };
    if (JSON.stringify(fill) !== JSON.stringify({ ...s, director: { name: d.name || "", rnokpp: d.rnokpp || "" } }) || !doc.seller) update((x) => ({ ...x, seller: { ...fill, source: s.source || ps.source } }));
  }, [pre.data, doc?.seller, canEdit]);
  const today = todayKyiv();
  const v = useMemo(() => (doc ? validatePn(doc, { today, buyerCheck }) : null), [doc, today, buyerCheck]);
  const errorsFor = (path) => (shown[stepOf(path)] ? v?.blocking.find((x) => x.path === path)?.message : null);

  // «Далі» з помилками на поточному кроці — показуємо їх і ставимо фокус на першу, не переходимо.
  // Кнопки кроків угорі (strict = false) дозволяють переходити вільно
  const go = async (n, strict = true) => {
    if (strict && n > step && step <= 2) {
      setShown((s) => ({ ...s, [step]: true }));
      const errs = v.blocking.filter((x) => stepOf(x.path) === step);
      // Фокус — на першу помилку за розташуванням на екрані, а не за порядком перевірок. Помилки без поля на екрані —
      // переліком над кнопками: «Далі» ніколи не мовчить
      if (errs.length) {
        setTimeout(() => {
          const found = errs.map((x) => [x, document.getElementById(fid(x.path))]);
          setStuck(found.filter(([, el]) => !el).map(([x]) => x.message));
          const els = found.map(([, el]) => el).filter(Boolean)
            .sort((x, y) => (x.compareDocumentPosition(y) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
          (els[0] || document.getElementById("pn-stuck"))?.focus();
        }, 0);
        return;
      }
    }
    setStuck([]);
    if (n >= 3) { setShown({ 1: true, 2: true }); await flushNow(); setDocKey((k) => k + 1); }
    setStep(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goTo = (path) => {
    setShown((s) => ({ ...s, [stepOf(path)]: true }));
    setStep(stepOf(path));
    setTimeout(() => { const el = document.getElementById(fid(path)) || document.getElementById(`${fid(path)}-0`); el?.focus(); el?.scrollIntoView({ block: "center" }); }, 50);
  };

  if (loadError) return <p className="error" role="alert">{loadError.status === 404 ? "Чернетку не знайдено" : loadError.message}</p>;
  if (!doc) return <p className="muted" role="status">Відкриваємо чернетку…</p>;
  const t = totals(doc.rows, doc.priceMode);
  const deadline = registrationDeadline(doc.date);

  return (
    <div className="pn">
      <div className="section-head">
        <div>
          <h2>{doc.number ? `Податкова накладна № ${doc.number}` : "Нова податкова накладна"}</h2>
          <p className="status">{KINDS[doc.kind]?.title} · <SaveState save={save} onReload={() => location.reload()} /></p>
        </div>
        <a className="btn btn-secondary btn-sm" href={`#/c/${companyId}/pn`}>Усі чернетки</a>
      </div>

      <ol className="pn-steps" aria-label="Кроки">
        {STEPS.map((s, i) => (
          <li key={s} className={step === i + 1 ? "on" : step > i + 1 ? "done" : ""}>
            <button type="button" onClick={() => go(i + 1, false)} aria-current={step === i + 1 ? "step" : undefined}>
              <span className="pn-step-n">{step > i + 1 ? "✓" : i + 1}</span>{s}
            </button>
          </li>
        ))}
      </ol>
      {!canEdit && <p className="card warn">Роль «перегляд»: змінювати чернетку не можна.</p>}

      <div className={`pn-layout${step === 4 ? " wide" : ""}`}>
        <fieldset className="pn-main" disabled={!canEdit && step < 3}>
          {step === 1 && <StepParty companyId={companyId} doc={doc} update={update} errorsFor={errorsFor} pre={pre.data}
            today={today} buyerCheck={buyerCheck} setBuyerCheck={setBuyerCheck} />}
          {step === 2 && canEdit && <PnFromPrimary companyId={companyId} draftId={draftId} doc={doc} update={update} />}
          {step === 2 && <PnRows companyId={companyId} doc={doc} update={update} errorsFor={errorsFor}
            rowErrors={shown[2] ? v.blocking.filter((x) => x.path.startsWith("rows.")) : []} showErrors={() => setShown((s) => ({ ...s, 2: true }))} />}
          {step === 2 && <Oracle companyId={companyId} draftId={draftId} docKey={savedVer} hasRows={doc.rows.some((r) => r.name.trim())} />}
          {step === 3 && <StepCheck companyId={companyId} draftId={draftId} docKey={docKey} doc={doc} t={t} goTo={goTo} onNext={() => go(4)} />}
          {step === 4 && <Step4 companyId={companyId} company={company} draftId={draftId} docKey={docKey} setDocKey={setDocKey} onBack={() => go(3)} onRelogin={onRelogin} />}
          {step < 3 && stuck.length > 0 && (
            <div className="notice-error" id="pn-stuck" tabIndex={-1} role="alert"><b>Щоб перейти далі, виправте:</b><ul>{stuck.map((m, i) => <li key={i}>{m}</li>)}</ul></div>
          )}
          {step < 3 && (
            <div className="actions pn-nav">
              {step > 1 && <button type="button" className="btn btn-secondary" onClick={() => go(step - 1)}>← Назад</button>}
              <button type="button" className="btn" onClick={() => go(step + 1)}>{step === 1 ? "Далі: товари →" : "Далі: перевірка →"}</button>
            </div>
          )}
        </fieldset>
        {step < 4 && <Summary doc={doc} t={t} deadline={deadline} today={today} v={v} onNext={step < 3 ? () => go(step + 1) : null} />}
      </div>
    </div>
  );
}

function SaveState({ save, onReload }) {
  if (save.state === "saving" || save.state === "dirty") return <span aria-live="polite">зберігаємо…</span>;
  if (save.state === "error") return save.conflict
    ? <span className="error" role="alert">чернетку змінено в іншій вкладці — <button type="button" className="linkish" onClick={onReload}>оновити</button></span>
    : <span className="error" role="alert">не збережено: {save.error.message}</span>;
  return <span aria-live="polite">чернетка збережена{save.at ? ` о ${save.at.toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit" })}` : ""}</span>;
}

// --- ① Операція й покупець ----------------------------------------------------------------------
function StepParty({ companyId, doc, update, errorsFor, pre, today, buyerCheck, setBuyerCheck }) {
  const set = (patch) => update((d) => ({ ...d, ...patch }));
  const setBuyer = (patch) => update((d) => ({ ...d, buyer: { ...d.buyer, ...patch } }));
  const s = doc.seller;
  const Err = ({ path }) => (errorsFor(path) ? <span className="pn-err" id={`${fid(path)}-err`}>{errorsFor(path)}</span> : null);
  const a = (path) => ({ id: fid(path), "aria-invalid": Boolean(errorsFor(path)) || undefined, "aria-describedby": errorsFor(path) ? `${fid(path)}-err` : undefined });
  const deadline = registrationDeadline(doc.date);

  return (
    <>
      <section className="card" aria-labelledby="pn-who-h">
        <h3 id="pn-who-h">Хто кому продає</h3>
        <div className="pn-two">
          <div>
            <p className="pn-label">Продавець</p>
            {s ? (
              <><p className="pn-filled"><b>{shortName(s.name)}</b><br />ІПН {s.ipn || "—"} · код {s.tin}<br />
                <span className="muted small">Підписує: {s.director?.name || "—"}</span><br />
                <span className="pn-src">✓ Підставлено {s.source}</span></p>
                {/* РНОКПП керівника в картці платника зазвичай немає (компанія ще не подавала ПН) — вводить людина */}
                {!/^\d{10}$/.test(String(s.director?.rnokpp || "")) && (
                  <label className="field">РНОКПП керівника (підписує ПН)
                    <input {...a("seller.director")} inputMode="numeric" maxLength={10} value={s.director?.rnokpp || ""}
                      onChange={(e) => update((d) => ({ ...d, seller: { ...d.seller, director: { ...d.seller.director, rnokpp: e.target.value.replace(/\D/g, "") } } }))}
                      // Збережемо в «Дані компанії» → «Відомості про керівників»: наступні ПН і запити візьмуть звідти
                      onBlur={(e) => { const v = e.target.value.replace(/\D/g, ""); if (/^\d{10}$/.test(v)) api(`/api/companies/${companyId}/card/officials`, { method: "PUT", body: { bosRnokpp: v } }).catch(() => {}); }} />
                    <span className="field-hint">10 цифр. У «Даних компанії» його ще немає — збережемо туди, і наступні документи візьмуть звідти</span><Err path="seller.director" /></label>
                )}
                {errorsFor("seller") && <p className="error small" id={fid("seller")} tabIndex={-1}>{errorsFor("seller")}</p>}</>
            ) : <p className="error" id={fid("seller")} tabIndex={-1}>Не вдалося підставити реквізити продавця: дані компанії (картку платника ДПС) ще не завантажено. Натисніть «Оновити усі розділи» вгорі.</p>}
          </div>
          <div>
            <p className="pn-label">Покупець</p>
            {doc.kind === "nonpayer" ? (
              doc.reason === "11" ? (
                <p className="pn-filled"><b>Неплатник — за підсумками дня</b><br />Продажі за чеками РРО/ПРРО{doc.daily?.date ? ` за ${doc.daily.date.split("-").reverse().join(".")}` : ""}.<br />
                  <span className="muted small">Умовний ІПН {NONPAYER.ipn}, тип причини 11 — ПН за щоденними підсумками операцій. Складається, лише якщо на ці операції ще не складено ПН; повернення за чеками — окремим розрахунком коригування.</span></p>
              ) : <p className="pn-filled"><b>Неплатник</b><br />Фізична особа або ФОП без ПДВ.<br />
                <span className="muted small">У ПН буде умовний ІПН {NONPAYER.ipn} і тип причини {NONPAYER.reason} — так вимагає Порядок. Покупцю ПН не надається, реєструєте її ви.</span></p>
            ) : (
              <BuyerFields companyId={companyId} b={doc.buyer} setBuyer={setBuyer} a={a} Err={Err} buyerCheck={buyerCheck} setBuyerCheck={setBuyerCheck} />
            )}
          </div>
        </div>
      </section>

      <section className="card" aria-labelledby="pn-when-h">
        <h3 id="pn-when-h">Дата й номер накладної</h3>
        <div className="pn-two">
          <div>
            <label className="field pn-date">{doc.kind === "prepay" ? "Дата оплати" : "Дата накладної"}
              <input type="date" {...a("date")} value={doc.date} max={today} onChange={(e) => set({ date: e.target.value, firstEvent: doc.kind === "prepay" ? "payment" : doc.firstEvent })} />
              <span className="field-hint">{doc.kind === "prepay"
                ? "День, коли гроші надійшли на рахунок, — з нього виникає ПДВ."
                : "Дата першої події: відвантаження (надання послуги) чи оплати — що сталося раніше. Отримали передоплату — це дата оплати. На суму не впливає, лише на дату й строк реєстрації."}</span>
              <Err path="date" />
              {deadline && <span className="pn-src">Зареєструвати до {ua(deadline)} (воєнний стан: складені 1–15 числа — до 5-го, з 16-го — до 18-го числа наступного місяця)</span>}
            </label>
            {doc.kind !== "prepay" && <DateHelper doc={doc} set={set} today={today} />}
          </div>
          <div>
            <label className="field pn-number">Номер накладної
              <input {...a("number")} inputMode="numeric" value={doc.number} onChange={(e) => set({ number: e.target.value.replace(/\D/g, "") })} />
              <span className="field-hint">Ваша власна нумерація ПН.{pre?.lastIssued ? ` Остання видана: № ${pre.lastIssued.number} від ${ua(pre.lastIssued.date)}.` : ""}</span>
              <Err path="number" />
            </label>
            {pre?.nextNumber && doc.number !== pre.nextNumber && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => set({ number: pre.nextNumber })}>Підставити № {pre.nextNumber}</button>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

// «Підкажіть дату»: людина вводить, що знає (відвантаження, оплата) — дата ПН = раніша з них
function DateHelper({ doc, set, today }) {
  const [open, setOpen] = useState(false);
  const [ship, setShip] = useState(""), [pay, setPay] = useState("");
  const first = [ship, pay].filter(Boolean).sort()[0] || "";
  const ev = first && first === pay && (!ship || pay < ship) ? "payment" : "shipment";
  return open ? (
    <div className="pn-helper">
      <p className="small">ПДВ виникає з першої події. Вкажіть, що знаєте, — дату ПН визначимо самі.</p>
      <div className="pn-two">
        <label className="field">Відвантажили / надали послугу<input type="date" max={today} value={ship} onChange={(e) => setShip(e.target.value)} /><span className="field-hint">за видатковою накладною чи актом</span></label>
        <label className="field">Отримали оплату<input type="date" max={today} value={pay} onChange={(e) => setPay(e.target.value)} /><span className="field-hint">за випискою банку; порожньо — ще не платили</span></label>
      </div>
      {first && <p className="pn-src">Перша подія — {ev === "payment" ? "оплата" : "відвантаження"} {ua(first)}. Це і є дата ПН.</p>}
      <div className="actions">
        <button type="button" className="btn btn-sm" disabled={!first} onClick={() => { set({ date: first, firstEvent: ev }); setOpen(false); }}>Поставити {first ? ua(first) : "дату"}</button>
        <button type="button" className="linkish" onClick={() => setOpen(false)}>закрити</button>
      </div>
    </div>
  ) : <button type="button" className="linkish" onClick={() => setOpen(true)}>Не знаєте, яку дату ставити? Підкажемо</button>;
}

function BuyerFields({ companyId, b, setBuyer, a, Err, buyerCheck, setBuyerCheck }) {
  const [checking, setChecking] = useState(false);
  const [checkErr, setCheckErr] = useState(null);
  const tin = String(b.tin || "").replace(/\s/g, "");
  const check = async (code = tin) => {
    setChecking(true); setCheckErr(null);
    try {
      const r = await api(`/api/companies/${companyId}/counterparties/${code}/check`, { method: "POST" });
      const pdv = r.check.verdict?.items?.find((x) => x.key === "pdv");
      const ipn = pdv?.detail?.match(/ІПН (\d{12})/)?.[1] || null;
      // Повна назва — як у реєстрі платників ПДВ для цього ІПН (з ним ЄРПН звіряє покупця); немає — з реєстру обліку
      const name = r.check.verdict?.vat?.history?.find((h) => h.vatNumber === ipn)?.name || r.check.verdict?.name || null;
      setBuyerCheck({ vat: pdv?.tone === "ok" ? true : pdv?.tone === "warn" ? null : false, title: pdv?.title, ipn, name, at: r.check.checkedAt });
      // Покупця щойно вибрано з пошуку без ІПН — підставляємо ІПН і назву з реєстру платників ПДВ
      if (code !== tin && ipn) setBuyer({ ipn, ...(name ? { name } : {}), source: "з ЄДР і реєстру платників ПДВ" });
    } catch (e) { setCheckErr(e); setBuyerCheck({ vat: null, error: e.message }); }
    finally { setChecking(false); }
  };
  const loadBuyers = (q) => api(`/api/companies/${companyId}/pn/buyers?q=${encodeURIComponent(q)}`).then((r) => r.buyers);
  return (
    <>
      <label className="field">Знайти покупця
        <SuggestInput id={fid("buyer.search")} value={b.search || ""} label="Пошук покупця за назвою, кодом або ІПН" minLength={2}
          placeholder="Назва, ЄДРПОУ або ІПН — у ваших ПН, контрагентах і ЄДР" onChange={(q) => setBuyer({ search: q })} load={loadBuyers}
          onPick={(x) => {
            setBuyer({ name: x.name || "", tin: x.tin, ipn: x.ipn || "", search: "", source: { own: "з ваших ПН", list: "з ваших контрагентів", edr: "з ЄДР" }[x.from] || "з ваших ПН" });
            setBuyerCheck(x.check ? { ...x.check } : null);
            // ІПН невідомий (ЄДР, список контрагентів) — одразу перевіряємо в реєстрі платників ПДВ: звідти ІПН і назва для ПН
            if (!x.ipn && !x.check) check(x.tin);
          }}
          render={(x) => (<><span>{shortName(x.name) || x.tin}</span><span className="muted small">код {x.tin}{x.ipn ? ` · ІПН ${x.ipn}` : ""}{x.sold ? ` · продавали ${x.sold} раз` : ""}{x.check?.vat === true ? " · ✓ платник ПДВ" : ""}{x.from === "edr" ? ` · ЄДР${x.stan ? `: ${x.stan}` : ""}` : x.from === "list" ? " · ваші контрагенти" : ""}</span></>)} />
        <span className="field-hint">Юрособу шукаємо і в ЄДР — за назвою (від 3 літер) або кодом. ФОП — введіть РНОКПП нижче й перевірте в реєстрі.</span>
      </label>
      <div className="pn-grid2">
        <label className="field pn-span2">Назва покупця<input {...a("buyer.name")} value={b.name} onChange={(e) => setBuyer({ name: e.target.value, source: null })} /><Err path="buyer.name" /></label>
        <label className="field">Код ЄДРПОУ або РНОКПП<input {...a("buyer.tin")} inputMode="numeric" maxLength={10} value={b.tin}
          onChange={(e) => { setBuyer({ tin: e.target.value.replace(/\D/g, ""), source: null }); setBuyerCheck(null); }} /><Err path="buyer.tin" /></label>
        <label className="field">ІПН (12 цифр)<input {...a("buyer.ipn")} inputMode="numeric" maxLength={12} value={b.ipn}
          onChange={(e) => setBuyer({ ipn: e.target.value.replace(/\D/g, ""), source: null })} /><Err path="buyer.ipn" /></label>
      </div>
      {b.source && <p className="pn-src">✓ Дані покупця {b.source}</p>}
      <div className="pn-check">
        <button type="button" className="btn btn-secondary btn-sm" disabled={!/^(\d{8}|\d{10})$/.test(tin) || checking} onClick={() => check()}>
          {checking ? "Перевіряємо в реєстрі…" : "Перевірити в реєстрі платників ПДВ"}</button>
        {buyerCheck && !buyerCheck.error && (
          <span className={buyerCheck.vat ? "pn-ok" : buyerCheck.vat === false ? "error" : "muted"} role="status">
            {buyerCheck.vat ? "✓ " : ""}{buyerCheck.title}{buyerCheck.at ? ` (перевірено ${new Date(buyerCheck.at).toLocaleString("uk-UA")})` : ""}
          </span>
        )}
        {checkErr && <span className="muted small" role="status">Реєстр недоступний: {checkErr.message}. Можна продовжити — статус буде позначено як неперевірений.</span>}
        {buyerCheck?.ipn && (buyerCheck.ipn !== b.ipn || (buyerCheck.name && buyerCheck.name.trim() !== String(b.name || "").trim())) && (
          <span className="pn-src">У реєстрі: {buyerCheck.name ? `${buyerCheck.name}, ` : ""}ІПН {buyerCheck.ipn}.{" "}
            <button type="button" className="linkish" onClick={() => setBuyer({ ipn: buyerCheck.ipn, ...(buyerCheck.name ? { name: buyerCheck.name } : {}), source: "з реєстру платників ПДВ" })}>Підставити</button></span>
        )}
      </div>
    </>
  );
}

// --- ③ Перевірка ------------------------------------------------------------------------------
function StepCheck({ companyId, draftId, docKey, doc, t, goTo, onNext }) {
  const d = useResource(`/api/companies/${companyId}/pn/drafts/${draftId}/document`, docKey);
  if (d.pending) return <p className="muted" role="status">Перевіряємо документ…</p>;
  if (d.error) return <p className="error" role="alert">{d.error.message}</p>;
  const { blocking, warnings, unverified } = d.data.validation;
  const buyer = doc.kind === "nonpayer" ? "покупець без ПДВ (накладна не надається покупцю)" : shortName(doc.buyer.name) || "покупця не вказано";
  const Group = ({ title, items, cls }) => (items.length > 0 && (
    <section className={`pn-issues ${cls}`} aria-label={title}>
      <h4>{title} ({items.length})</h4>
      <ul>{items.map((x, i) => (
        <li key={i}><span>{x.message}</span>{x.path !== "seller" && x.path !== "seller.director" && <button type="button" className="linkish" onClick={() => goTo(x.path)}>до поля →</button>}</li>
      ))}</ul>
    </section>
  ));
  return (
    <section className="card" aria-labelledby="pn-check-h">
      <h3 id="pn-check-h">Перевірка перед підписом</h3>
      <p className="pn-resume">{buyer} отримає ПН {doc.number ? `№ ${doc.number}` : "без номера"}{doc.date ? ` від ${ua(doc.date)}` : ""} за {doc.rows.filter((r) => r.name.trim()).length} позиц.<br />
        Без ПДВ <b>{kopUi(t.base)}</b> · ПДВ <b>{kopUi(t.vat)}</b> · Разом <b>{kopUi(t.total)}</b> грн</p>
      <Group title="Треба виправити" items={blocking} cls="bad" />
      <Group title="Зверніть увагу" items={warnings} cls="warn" />
      <Group title="Не вдалося перевірити" items={unverified} cls="info" />
      {!blocking.length && <p className="pn-ok">✓ Помилок, що заважають подати накладну, немає.</p>}
      <div className="actions pn-nav">
        <button type="button" className="btn" disabled={blocking.length > 0} onClick={onNext}>Переглянути податкову накладну →</button>
        {blocking.length > 0 && <span className="muted small">Спершу виправте помилки — кнопка стане доступною.</span>}
      </div>
    </section>
  );
}

// «Оракул»: прогноз СМКОР (п.1 критеріїв ризиковості, ПКМУ № 1165) за даними ЄРПН/СЕА — як у etax
const grn = (v) => Math.round(v).toLocaleString("uk-UA");
// Стани прогнозу: є безумовна ознака / ризику не виявлено / ризик зупинення / недостатньо даних
const ORACLE_CLASS = { unconditional: "ok", clear: "ok", risk: "bad", unknown: "unk" };
const ORACLE_ICON = { unconditional: "✓", clear: "✓", risk: "⛔", unknown: "?" };
function Oracle({ companyId, draftId, docKey, hasRows }) {
  const o = useResource(hasRows ? `/api/companies/${companyId}/pn/drafts/${draftId}/oracle` : null, docKey);
  const head = <h4 id="pn-oracle-h">Оракул: прогноз реєстрації <span className="muted small">(СМКОР, ПКМУ № 1165){o.updating ? " · перераховуємо…" : ""}</span></h4>;
  if (!hasRows) return <section className="pn-oracle" aria-labelledby="pn-oracle-h">{head}<p className="muted small">Додайте товари чи послуги — тут з'явиться прогноз, чи зареєструє ДПС цю ПН.</p></section>;
  if (o.pending) return <p className="muted small" role="status">Оракул рахує прогноз…</p>;
  if (o.error && !o.data) return <p className="error small" role="alert">Оракул: {o.error.message}</p>;
  if (!o.data) return <p className="muted small" role="status">Оракул рахує прогноз…</p>;
  const { verdict: v, caveat, cascade, ppiDate } = o.data;
  const mark = (ok) => (ok === true ? "✓" : ok === false ? "✗" : "?");
  const unc = (v.unconditional || []).filter((x) => !x.na);
  return (
    <section className={`pn-oracle ${ORACLE_CLASS[v.state] || "unk"}`} aria-labelledby="pn-oracle-h" aria-busy={o.updating}>
      {head}
      <p className="pn-oracle-v" role="status">{ORACLE_ICON[v.state] || "?"} {v.title}</p>
      {v.reasons.length > 0 && <ul className="small">{v.reasons.map((x, i) => <li key={i}>{x}</li>)}</ul>}
      {v.warnings?.length > 0 && <div className="pn-oracle-warn small" role="note"><b>Увага:</b><ul>{v.warnings.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
      {v.advice.length > 0 && <><p className="small"><b>Що зробити:</b></p><ol className="small">{v.advice.map((x, i) => <li key={i}>{x}</li>)}</ol></>}
      {unc.length > 0 && (
        <details className="pn-oracle-unc" open={Boolean(v.hit)}>
          <summary className="small"><b>Ознаки безумовної реєстрації (п.3 Порядку)</b> — {v.hit ? `діє пп.${v.hit}` : "жодна не діє"}{unc.some((x) => x.ok === null) ? ", частину не вдалося перевірити" : ""}</summary>
          <ol className="small">{unc.map((x) => (
            <li key={x.n} className={x.ok === true ? "ok" : x.ok === false ? "no" : "unk"}>
              <span className="pn-oracle-m" aria-hidden="true">{mark(x.ok)}</span> <b>пп.{x.n}</b> {x.title}
              <ul>{x.conds.map((c, i) => <li key={i} className={c.ok === true ? "ok" : c.ok === false ? "no" : "unk"}><span aria-label={c.ok === true ? "так" : c.ok === false ? "ні" : "невідомо"}>{mark(c.ok)}</span> {c.label}</li>)}</ul>
            </li>))}
          </ol>
        </details>
      )}
      {v.groups.length > 0 && (
        <table className="pn-oracle-t">
          <caption className="small">Критерій 1: залишок за кодами (придбання з ПДВ і ввезення з 2017 р.){v.hit ? " — довідково" : ""}</caption>
          <thead><tr><th>Група / послуга</th><th>Зарахований вхід</th><th>Уже продано</th><th>Доступно (×1,5)</th><th>Ця ПН</th><th>Залишок</th></tr></thead>
          <tbody>{v.groups.map((g) => (
            <tr key={g.label} className={g.ok || g.inTable ? "" : "bad"}>
              <td data-l="Група / послуга">{g.label}{g.risky && <span className="pn-oracle-risky" title="Група з переліку ризикових товарів СМКОР"> ризикова</span>}
                <div className="muted small">{g.inTable ? "у врахованій таблиці даних — критерій 1 не перевіряє" : g.ok ? "залишку вистачає" : `не вистачає ${grn(g.need - g.limit)} грн`}{!g.inTable && g.tableNeed > 0 ? ` · ще ${grn(g.tableNeed)} грн — коди з таблиці даних` : ""}</div></td>
              <td data-l="Зарахований вхід">{grn(g.inflow)}</td>
              <td data-l="Уже продано">{grn(g.sold)}</td>
              <td data-l="Доступно (×1,5)">{grn(g.limit)}</td>
              <td data-l="Ця ПН">{grn(g.need + (g.tableNeed || 0))}</td>
              <td data-l="Залишок"><b>{g.inTable ? "—" : grn(g.limit - g.need)}</b></td>
            </tr>))}</tbody>
        </table>
      )}
      <details className="small muted">
        <summary>Як рахує Оракул</summary>
        <p>{cascade}</p>
        <p>{caveat}</p>
        {ppiDate && <p>Ознаки позитивної історії — зі знімка кабінету на {ppiDate.split("-").reverse().join(".")}. Оновіть розділ «СМКОР», щоб узяти свіжі.</p>}
      </details>
    </section>
  );
}

function Step4({ companyId, company, draftId, docKey, setDocKey, onBack, onRelogin }) {
  const d = useResource(`/api/companies/${companyId}/pn/drafts/${draftId}/document`, docKey);
  return <PnForm companyId={companyId} company={company} draftId={draftId} docRes={d} onBack={onBack} onRelogin={onRelogin} onReviewed={() => setDocKey((k) => k + 1)} />;
}

// --- Підсумок (праворуч; на телефоні — закріплена панель знизу) ----------------------------------------
function Summary({ doc, t, deadline, today, v, onNext }) {
  const rates = Object.entries(t.by);
  const left = deadline ? Math.round((Date.parse(deadline) - Date.parse(today)) / 86_400_000) : null;
  return (
    <aside className="pn-summary" aria-label="Підсумок накладної">
      <h3>Підсумок</h3>
      <dl className="pn-sum">
        <div><dt>Кому</dt><dd>{doc.kind === "nonpayer" ? "Неплатник" : shortName(doc.buyer?.name) || "—"}</dd></div>
        <div><dt>Дата</dt><dd>{ua(doc.date) || "—"}</dd></div>
        <div><dt>№ ПН</dt><dd>{doc.number || "—"}</dd></div>
        {rates.map(([r, x]) => <div key={r} className="pn-sum-rate"><dt>Без ПДВ{rates.length > 1 ? ` (${RATES[r].label})` : ""}</dt><dd>{kopUi(x.base)}</dd></div>)}
        {rates.filter(([r]) => RATES[r].pct > 0n).map(([r, x]) => <div key={`v${r}`}><dt>ПДВ {RATES[r].label}</dt><dd>{kopUi(x.vat)}</dd></div>)}
        <div className="pn-sum-total"><dt>Разом</dt><dd><Money text={kopUi(t.total)} /> грн</dd></div>
      </dl>
      {deadline && <p className={`small ${left != null && left < 0 ? "error" : "muted"}`}>Зареєструвати до {ua(deadline)}{left != null ? (left >= 0 ? ` (ще ${left} дн.)` : " — строк минув") : ""}</p>}
      {v && <p className="small muted">{v.blocking.length ? `Треба виправити: ${v.blocking.length}` : "✓ Помилок немає"}{v.warnings.length ? ` · попереджень: ${v.warnings.length}` : ""}</p>}
      {onNext && <button type="button" className="btn btn-block pn-sum-next" onClick={onNext}>Далі →</button>}
    </aside>
  );
}
